import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    Activity, Bot, ChevronDown, Eye, FileUp, FlaskConical, Loader2, ShieldAlert, Trash2, UserSearch,
} from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { EngineHero } from "@/components/common/EngineHero";
import { SeverityBadge } from "@/components/common/Badges";
import { ReasonDialog } from "@/components/common/ReasonDialog";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { TID } from "@/lib/testIds";

const DECISIONS = [
    { key: "accept_risk", label: "Accept risk" },
    { key: "remediated", label: "Remediated" },
    { key: "escalate", label: "Escalate" },
    { key: "dismiss", label: "False positive" },
];

const VERDICT_TONE = {
    control_gap_confirmed: "text-rose-300 border-rose-500/30 bg-rose-500/10",
    escalate: "text-rose-300 border-rose-500/30 bg-rose-500/10",
    needs_business_confirmation: "text-amber-300 border-amber-500/30 bg-amber-500/10",
    likely_benign: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
};

function riskTone(score) {
    if (score >= 80) return "text-rose-400";
    if (score >= 40) return "text-amber-400";
    if (score > 0) return "text-cyan-300";
    return "text-slate-400";
}

function CaseFile({ c, anomalyId }) {
    return (
        <div data-testid={TID.uebaCaseFile(anomalyId)} className="border-t border-white/5 pt-2 mt-2 space-y-2 text-[11px]">
            <div className="flex flex-wrap items-center gap-2">
                <Bot size={12} className="text-cyan-400" />
                <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${VERDICT_TONE[c.verdict] || "text-slate-300 border-white/10 bg-white/5"}`}>
                    {String(c.verdict || "").replace(/_/g, " ")}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">confidence {c.confidence}</span>
                <span className="font-mono text-[9px] text-slate-400">{String(c.generated_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="text-slate-200">{c.hypothesis}</p>

            <div className="space-y-1">
                <p className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Evidence chain</p>
                {(c.steps || []).map((s) => (
                    <div key={s.step} className="flex gap-2">
                        <span className="font-mono text-cyan-300 shrink-0">{s.step}.</span>
                        <span className="text-slate-300">{s.action} — <span className="text-slate-400">{s.found}</span></span>
                    </div>
                ))}
            </div>

            {(c.recommended_actions || []).length > 0 && (
                <div className="space-y-1">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Draft change set (needs human approval)</p>
                    {c.recommended_actions.map((a, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 text-slate-300">
                            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm border bg-white/5 border-white/10 text-slate-300">{a.effort}</span>
                            <span className="flex-1 min-w-[220px]">{a.action}</span>
                            <span className="font-mono text-[9px] text-slate-400">{a.owner}</span>
                        </div>
                    ))}
                </div>
            )}

            {(c.does_not_support || []).length > 0 && (
                <div className="space-y-0.5">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-amber-300">What the evidence does NOT support</p>
                    {c.does_not_support.map((d, i) => <p key={i} className="text-slate-400">· {d}</p>)}
                </div>
            )}
            <p className="text-[10px] font-mono text-slate-400">{c.engine_note} {c.disclaimer}</p>
        </div>
    );
}

function AnomalyCard({ a, onInvestigate, onDecide, busy }) {
    const [open, setOpen] = useState(false);
    return (
        <motion.div variants={staggerItem} data-testid={TID.uebaAnomaly(a.id)} className="panel p-3 rounded-[4px] space-y-2">
            <div className="flex flex-wrap items-start gap-2">
                <SeverityBadge severity={a.severity} />
                <span className="font-mono text-[10px] text-cyan-300">{a.detector}</span>
                <span className="font-mono text-[10px] text-slate-300">{a.user}</span>
                {a.status !== "open" && (
                    <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-white/5 text-slate-300 border-white/10">
                        {String(a.status).replace(/_/g, " ")}
                    </span>
                )}
                <span className="flex-1 min-w-[220px] text-sm text-slate-100">{a.title}</span>
            </div>
            <p className="text-[11px] text-slate-400">{a.detail}</p>
            <p className="text-[10px] text-amber-200/80">Not saying: {a.not_saying}</p>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    data-testid={TID.uebaInvestigate(a.id)}
                    onClick={() => onInvestigate(a)}
                    disabled={busy}
                    className="btn-primary inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
                >
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Bot size={11} />}
                    {a.case_file ? "Re-investigate" : "Investigate"}
                </button>
                {DECISIONS.map((d) => (
                    <button
                        key={d.key}
                        data-testid={TID.uebaDecision(a.id, d.key)}
                        onClick={() => onDecide(a, d.key)}
                        disabled={busy}
                        className="btn-ghost text-[10px] disabled:opacity-50"
                    >
                        {d.label}
                    </button>
                ))}
                <button
                    data-testid={TID.uebaEvidenceToggle(a.id)}
                    onClick={() => setOpen((v) => !v)}
                    className="btn-ghost inline-flex items-center gap-1 text-[10px]"
                >
                    <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Evidence
                </button>
            </div>

            {open && (
                <pre className="font-mono text-[10px] text-slate-300 bg-black/40 border border-white/5 rounded-sm p-2 overflow-x-auto">
                    {JSON.stringify(a.evidence, null, 1)}
                </pre>
            )}
            {a.case_file && <CaseFile c={a.case_file} anomalyId={a.id} />}
            {a.decision_reason && (
                <p className="text-[10px] font-mono text-slate-400">decision: {a.status} · {a.decision_reason} · {a.decided_by}</p>
            )}
        </motion.div>
    );
}

export default function Ueba() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [entity, setEntity] = useState(null);
    const [pending, setPending] = useState(null);   // { anomaly, decision } awaiting a reason
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/ueba/overview");
            setData(data);
        } catch {
            toast.error("Could not load behaviour analytics");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const upload = async (file) => {
        if (!file) return;
        setSeeding(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/ueba/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${data.events_ingested} event(s) ingested · ${data.anomalies_open} anomaly/anomalies open`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Log intake failed");
        } finally {
            setSeeding(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const seedDemo = async () => {
        setSeeding(true);
        try {
            const { data } = await api.post("/ueba/seed-demo");
            toast.success(`Synthetic log seeded · ${data.anomalies_open} anomaly/anomalies detected`);
            await load();
        } catch {
            toast.error("Could not seed the demo log");
        } finally {
            setSeeding(false);
        }
    };

    const purge = async () => {
        if (!window.confirm("Purge synthetic demonstration behaviour data?")) return;
        try {
            const { data } = await api.delete("/ueba/data?synthetic_only=true");
            toast.success(`Purged ${data.events_deleted} synthetic event(s)`);
            setEntity(null);
            await load();
        } catch {
            toast.error("Purge failed");
        }
    };

    const investigate = async (a) => {
        setBusyId(a.id);
        try {
            const { data } = await api.post(`/ueba/anomalies/${a.id}/investigate`);
            toast.success(`Case file ready · ${String(data.case_file?.verdict || "").replace(/_/g, " ")}`);
            await load();
        } catch {
            toast.error("Investigation failed");
        } finally {
            setBusyId(null);
        }
    };

    const decide = (a, decision) => setPending({ anomaly: a, decision });

    const submitDecision = async (reason) => {
        const { anomaly, decision } = pending;
        setPending(null);
        setBusyId(anomaly.id);
        try {
            await api.post(`/ueba/anomalies/${anomaly.id}/decision`, { decision, reason });
            toast.success("Decision recorded", { duration: 9000 });
            await load();
        } catch {
            toast.error("Could not record the decision");
        } finally {
            setBusyId(null);
        }
    };

    const openEntity = async (user) => {
        try {
            const { data } = await api.get(`/ueba/entities/${user}`);
            setEntity(data);
        } catch {
            toast.error("No activity for that user");
        }
    };

    return (
        <ModuleGate module="ueba" label="Behaviour Analytics (UEBA) & Agentic Investigation">
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-6" data-testid={TID.uebaPage}>
                <EngineHero
                    testId={TID.uebaHero}
                    engine="Behaviour analytics"
                    title="UEBA & Agentic Investigation"
                    icon={Activity}
                    blurb="Behavioural baselines built from your own SM20/RSAU audit log and STAD usage exports — off-hours concentration, first use of sensitive access, denial storms, peer deviation, bulk extraction, and the one that matters most in SAP: a segregation-of-duties conflict actually exercised at runtime. Each anomaly can be handed to the agentic investigator, which gathers evidence across the role library, the SoD rulebook, best-practice conformance and runtime traces before writing an approval-gated case file."
                    rules={(data?.counts?.by_detector ? Object.keys(data.counts.by_detector) : []).map((d) => ({ label: d, category: d }))}
                    totals={data?.counts?.by_detector || {}}
                />

                <Panel
                    eyebrow="Intake"
                    title="Activity log intake"
                    testId={TID.uebaIntake}
                    action={
                        <div className="flex flex-wrap items-center gap-2">
                            <input
                                ref={fileRef} type="file" accept=".txt,.csv,.log,.tsv"
                                onChange={(e) => upload(e.target.files?.[0])} className="hidden"
                                data-testid={TID.uebaUploadInput}
                            />
                            <button
                                data-testid={TID.uebaUpload}
                                onClick={() => fileRef.current?.click()}
                                disabled={seeding}
                                className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                            >
                                {seeding ? <Loader2 size={12} className="animate-spin" /> : <FileUp size={12} />} Upload SM20 / STAD export
                            </button>
                            <button
                                data-testid={TID.uebaSeedDemo}
                                onClick={seedDemo}
                                disabled={seeding}
                                className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                            >
                                <FlaskConical size={12} /> Seed synthetic log
                            </button>
                            {data?.has_data && (
                                <button data-testid={TID.uebaPurge} onClick={purge} className="btn-ghost inline-flex items-center gap-1.5 text-xs">
                                    <Trash2 size={12} /> Purge synthetic
                                </button>
                            )}
                        </div>
                    }
                >
                    {loading ? (
                        <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</p>
                    ) : !data?.has_data ? (
                        <p className="text-sm text-slate-400">{data?.note}</p>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
                                <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                                    {data.population.events} events
                                </span>
                                <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                                    {data.population.users} users
                                </span>
                                <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                                    {String(data.population.period_start).slice(0, 10)} → {String(data.population.period_end).slice(0, 10)}
                                </span>
                                {Object.entries(data.population.by_class).map(([k, v]) => (
                                    <span key={k} className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10">
                                        {k.replace(/_/g, " ")} · {v}
                                    </span>
                                ))}
                                {data.synthetic_only && (
                                    <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/25">
                                        synthetic demonstration data
                                    </span>
                                )}
                            </div>
                            <div data-testid={TID.uebaHourChart}>
                                <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={160}>
                                    <BarChart data={data.activity_by_hour}>
                                        <CartesianGrid strokeDasharray="2 4" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                        <XAxis dataKey="hour" stroke="#94a3b8" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
                                        <YAxis stroke="#94a3b8" allowDecimals={false} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "#0B1224", border: "1px solid rgba(148,163,184,0.2)", fontSize: 11 }} />
                                        <Bar dataKey="events" fill="var(--accent-1)" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <p className="text-[10px] font-mono text-slate-400">{data.method}</p>
                        </div>
                    )}
                </Panel>

                {data?.has_data && (
                    <>
                        <Panel eyebrow="Entity risk" title="Users ranked by behavioural risk" testId={TID.uebaEntities}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-[11px]">
                                    <thead>
                                        <tr className="text-left font-mono uppercase tracking-wider text-[9px] text-slate-400 border-b border-white/10">
                                            <th className="py-1.5 pr-3">User</th>
                                            <th className="py-1.5 pr-3">Risk</th>
                                            <th className="py-1.5 pr-3">Anomalies</th>
                                            <th className="py-1.5 pr-3">Events</th>
                                            <th className="py-1.5 pr-3">Off-hours</th>
                                            <th className="py-1.5 pr-3">Top transactions</th>
                                            <th className="py-1.5" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.entities.map((e) => (
                                            <tr key={e.user} data-testid={TID.uebaEntityRow(e.user)} className="border-b border-white/5 hover:bg-white/[0.02]">
                                                <td className="py-1.5 pr-3 font-mono text-slate-200">{e.user}</td>
                                                <td className={`py-1.5 pr-3 font-mono ${riskTone(e.risk_score)}`}>{e.risk_score}</td>
                                                <td className="py-1.5 pr-3 font-mono text-slate-300">{e.anomalies}</td>
                                                <td className="py-1.5 pr-3 font-mono text-slate-400">{e.events}</td>
                                                <td className="py-1.5 pr-3 font-mono text-slate-400">{e.off_hours_share}%</td>
                                                <td className="py-1.5 pr-3 font-mono text-slate-400">{e.top_tcodes.join(", ")}</td>
                                                <td className="py-1.5">
                                                    <button
                                                        data-testid={TID.uebaEntityOpen(e.user)}
                                                        onClick={() => openEntity(e.user)}
                                                        className="btn-ghost inline-flex items-center gap-1 text-[10px]"
                                                    >
                                                        <UserSearch size={11} /> Profile
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>

                        {entity && (
                            <Panel
                                eyebrow={`Entity profile · ${entity.user}`}
                                title="Behavioural profile"
                                testId={TID.uebaEntityDetail}
                                action={<button data-testid={TID.uebaEntityClose} onClick={() => setEntity(null)} className="btn-ghost text-[10px]">Close</button>}
                            >
                                <div className="space-y-2 text-[11px]">
                                    <div className="flex flex-wrap gap-2 font-mono text-[10px]">
                                        <span className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-slate-300">{entity.profile.events} events</span>
                                        <span className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-slate-300">{entity.profile.active_days} active days</span>
                                        <span className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-slate-300">off-hours {entity.profile.off_hours_share}%</span>
                                        <span className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-slate-300">weekend {entity.profile.weekend_events}</span>
                                    </div>
                                    <p className="text-slate-400">
                                        Transactions: {Object.entries(entity.profile.tcodes).slice(0, 12).map(([t, n]) => `${t}×${n}`).join("  ")}
                                    </p>
                                    <p className="text-slate-400">
                                        Roles in the library granting these transactions:{" "}
                                        {entity.granting_roles.length ? entity.granting_roles.map((r) => r.name).join(", ") : "none imported"}
                                    </p>
                                    <p className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Last events</p>
                                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                                        {entity.timeline.slice(-25).reverse().map((t, i) => (
                                            <p key={i} className="font-mono text-[10px] text-slate-400">
                                                {t.timestamp.slice(0, 16).replace("T", " ")} · {t.tcode || "—"} · {t.event_class} · {t.text.slice(0, 70)}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </Panel>
                        )}

                        <Panel
                            eyebrow="Awaiting decision"
                            title="Behavioural anomalies"
                            testId={TID.uebaAnomalies}
                            action={
                                <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
                                    {Object.entries(data.counts.by_severity || {}).map(([s, n]) => (
                                        <span key={s} className="flex items-center gap-1"><SeverityBadge severity={s} /><span className="text-slate-300">{n}</span></span>
                                    ))}
                                    <span className="text-slate-400">· {data.counts.decided} decided</span>
                                </div>
                            }
                        >
                            {data.anomalies.length === 0 ? (
                                <p className="text-sm text-emerald-300 flex items-center gap-2">
                                    <ShieldAlert size={14} /> No open behavioural anomalies in the current observation window.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {data.anomalies.map((a) => (
                                        <AnomalyCard key={a.id} a={a} busy={busyId === a.id} onInvestigate={investigate} onDecide={decide} />
                                    ))}
                                </div>
                            )}
                        </Panel>

                        {data.decided.length > 0 && (
                            <Panel eyebrow="Decided" title="Closed anomalies" testId={TID.uebaDecided}>
                                <div className="space-y-3">
                                    {data.decided.map((a) => (
                                        <div key={a.id} className="border-b border-white/5 pb-2 last:border-0">
                                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                                <Eye size={11} className="text-slate-500" />
                                                <SeverityBadge severity={a.severity} />
                                                <span className="font-mono text-[10px] text-cyan-300">{a.detector}</span>
                                                <span className="font-mono text-[10px] text-slate-300">{a.user}</span>
                                                <span className="flex-1 min-w-[200px] text-slate-300">{a.title}</span>
                                                <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{String(a.status).replace(/_/g, " ")}</span>
                                                <span className="font-mono text-[9px] text-slate-400">{a.decision_reason}</span>
                                            </div>
                                            {a.case_file && <CaseFile c={a.case_file} anomalyId={a.id} />}
                                        </div>
                                    ))}
                                </div>
                            </Panel>
                        )}
                    </>
                )}
            </motion.div>

            <ReasonDialog
                open={!!pending}
                testId={TID.uebaDecisionDialog}
                title={pending ? `Record decision · ${String(pending.decision).replace(/_/g, " ")}` : ""}
                description={pending
                    ? `${pending.anomaly.detector} · ${pending.anomaly.user} — this decision and its reason are written to the audit trail.`
                    : ""}
                label="Reason"
                placeholder="e.g. Compensating control MC-014 is in place and tested for this user."
                confirmLabel="Record decision"
                onConfirm={submitDecision}
                onCancel={() => setPending(null)}
            />
        </ModuleGate>
    );
}
